import React from 'react'
import { useTranslation } from 'react-i18next'
import cx from 'clsx'
import { IconUserOff } from '@tabler/icons-react'

import type { OrgStructureRecord } from '@pages/Team/Members/types'

interface NameCellProps {
  record: OrgStructureRecord
}

// Name column: the member's name plus any availability markers. On-leave
// is a neutral text badge on purpose — leave can be sick leave, parental
// leave, sabbatical etc., so no vacation imagery (ADN-557).
const NameCell: React.FC<NameCellProps> = ({ record }) => {
  const { t } = useTranslation('org', { keyPrefix: 'tableRows' })
  const isSuspended = record.status === 'suspended'
  const substituteName = record.substitute?.full_name
  const substituteLabel = substituteName ? t('onLeaveSubstitute', { name: substituteName }) : undefined

  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
      {/* The greyed name alone carries no accessible meaning, so the icon
          is the one that names the state. */}
      {isSuspended && <IconUserOff role="img" aria-label={t('suspended')} className="h-4 w-4 shrink-0 text-red-500" />}
      <span className={cx({ 'text-gray-400': isSuspended })}>{record.full_name}</span>
      {record.is_vacation && (
        <>
          <span
            data-testid="on-leave-badge"
            title={substituteLabel}
            className="shrink-0 text-xs font-medium px-2 py-0.5 rounded-full border bg-gray-100 border-gray-300 text-gray-600"
          >
            {t('onLeave')}
          </span>
          {/* `title` is mouse-only — keyboard and screen-reader users get
              nothing from it. The substitute rides along as off-screen text
              so the row stays compact without hiding the information. An
              aria-label on the badge would not do: it is a plain span with
              no role, so assistive tech is not obliged to announce one. */}
          {substituteLabel && <span className="sr-only">{substituteLabel}</span>}
        </>
      )}
    </div>
  )
}

export default React.memo(NameCell)
